import { Component, OnInit, OnDestroy, ElementRef, ViewChild, HostListener } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MaterialModule } from '../material.module';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';

interface PageItem {
  module: string;
  group: string;
  label: string;
  route: string;
}

@Component({
  selector: 'app-auto-page-search',
  templateUrl: './auto-page-search.component.html',
  styleUrls: ['./auto-page-search.component.scss'],
  imports: [MaterialModule, ReactiveFormsModule, MatAutocompleteModule, CommonModule],
  standalone: true
})
export class AutoPageSearchComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;

  searchControl = new FormControl('');
  groupedOptions$!: Observable<{ group: string, pages: PageItem[] }[]>;
  allPages: PageItem[] = [];
  private menuChange$ = new BehaviorSubject<any[]>([]);
  private storageListener!: () => void;
  private subs: Subscription[] = [];

  constructor(private router: Router) {}

  ngOnInit(): void {
    // 🔹 Load from localStorage
    const localMenu = localStorage.getItem('menu');
    if (localMenu) this.menuChange$.next(JSON.parse(localMenu));

    // 🔹 Listen for storage updates
    this.storageListener = () => {
      const updatedMenu = localStorage.getItem('menu');
      if (updatedMenu) this.menuChange$.next(JSON.parse(updatedMenu));
    };
    window.addEventListener('storage', this.storageListener);

    // 🔹 Reactively update autocomplete
    const sub1 = this.menuChange$.subscribe(menu => {
      this.allPages = this.flattenMenu(menu || []);
      this.groupedOptions$ = this.searchControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterGroupedPages(value || ''))
      );
    });
    this.subs.push(sub1);
  }

  flattenMenu(menuList: any[]): PageItem[] {
    const result: PageItem[] = [];
    for (const module of menuList) {
      if (module.children) {
        for (const group of module.children) {
          if (group.children) {
            for (const page of group.children) {
              result.push({
                module: module.label,
                group: group.label,
                label: page.label,
                route: page.route
              });
            }
          }
        }
      }
    }
    return result;
  }

  filterGroupedPages(query: any) {
  const q = typeof query === 'string' ? query.toLowerCase() : '';
  const filtered = this.allPages.filter(p =>
    p.label.toLowerCase().includes(q)
  );
  const groups: { [key: string]: PageItem[] } = {};
  filtered.forEach(p => {
    const key = `${p.module} → ${p.group}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  });
  return Object.entries(groups).map(([group, pages]) => ({ group, pages }));
}

  displayFn(page: PageItem): string {
    return page && page.label ? page.label : '';
  }

  onPageSelect(page: PageItem) {
    if (page?.route) {
      this.router.navigate([page.route]).then(() => {
        this.searchControl.setValue('', { emitEvent: true });
        this.searchControl.markAsPristine();
        this.searchControl.markAsUntouched();
        this.autoTrigger.closePanel(); // ✅ close autocomplete
        this.searchControl.updateValueAndValidity();
      });
    }
  }

  // 🔥 HOTKEY LISTENER: Ctrl + M opens the search box
  @HostListener('window:keydown', ['$event'])
  handleHotKey(event: KeyboardEvent) {
    if (event.ctrlKey && event.key.toLowerCase() === 'm') {
      event.preventDefault();
      if (this.searchInput) {
        this.searchInput.nativeElement.focus();
        setTimeout(() => this.autoTrigger.openPanel(), 100);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
    this.subs.forEach(s => s.unsubscribe());
  }
}
